import { createStore, applyMiddleware, compose } from 'redux';
import { routerMiddleware } from 'react-router-redux';
import createSagaMiddleware from 'redux-saga';
import createHistory from 'history/createBrowserHistory';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import { KEY_PREFIX } from 'redux-persist/lib/constants';
import { setRouterParamsTabs } from 'redux/modules/routerParams';
import { uniqBy, differenceBy } from 'lodash';
import rootReducer, { rootSaga } from './modules';

export const history = createHistory();

const persistWhitelistFormNames = [
  'facilitiesFilter',
  'usersFilter',
  'referralsFilter',
  'salesRepresentativesFilter',
  'prescribersFilter',
  'vendorsFilter',
  'templatesFilter',
  'chartsFilter',
  'inventoryFilter',
  '-workflow-ordersFilter',
  'intake-ordersFilter',
  'delivery-ordersFilter',
  'billing-ordersFilter',
  'collection-ordersFilter',
  'archive-ordersFilter',
  'schedule-ordersFilter',
  '-ordersFilter',
  'diagnosesFilter',
  'hcpcsCodesFilter',
  'rolesFilter',
  'noteCategoriesFilter',
  '-workflowNavigationFilter',
  'claimsFilter',
  'workflowStatusesFilter',
  'notificationsFilter',
  'insurancesFilter',
  'claimStatusesFilter',
  'claimAdjustmentCodesFilter',
];

const persistFormTransform = createTransform(
  (inboundState) => {
    const newInboundState = Object.keys(inboundState).reduce((accumulator, currentValue) => {
      if (persistWhitelistFormNames
        .filter(formName => currentValue.indexOf(formName) !== -1).length > 0) {
        return {
          ...accumulator,
          [currentValue]: inboundState[currentValue],
        };
      }
      return accumulator;
    }, {});
    return newInboundState;
  },
  (outboundState) => {
    const newOutboundState = Object.keys(outboundState).reduce((accumulator, currentValue) => {
      if (persistWhitelistFormNames
        .filter(formName => currentValue.indexOf(formName) !== -1).length > 0) {
        return {
          ...accumulator,
          [currentValue]: outboundState[currentValue],
        };
      }
      return accumulator;
    }, {});
    return newOutboundState;
  },
  { whitelist: ['form'] },
);

const persistRouterParamsTransform = createTransform(
  (inboundState) => {
    const {
      record,
      tabs,
      ...otherState
    } = inboundState;
    return {
      ...otherState,
      tabs: {
        ...tabs,
        patient: uniqBy(tabs.patient, 'ID'),
      },
    };
  },
  (outboundState) => {
    const {
      record,
      tabs,
      ...otherState
    } = outboundState;
    return {
      ...otherState,
      tabs: {
        ...tabs,
        patient: uniqBy(tabs.patient, 'ID'),
      },
    };
  },
  { whitelist: ['routerParams'] },
);

const persistConfig = {
  key: 'dme4dme',
  storage,
  whitelist: ['routerParams', 'form', 'recoverOrder'],
  transforms: [
    persistFormTransform,
    persistRouterParamsTransform,
  ],
  stateReconciler: autoMergeLevel2,
};

const initialState = {};
const enhancers = [];
const sagaMiddleware = createSagaMiddleware();
const middleware = [
  routerMiddleware(history),
  sagaMiddleware,
];

if (process.env.NODE_ENV === 'development') {
  const devToolsExtension = window.devToolsExtension;
  if (typeof devToolsExtension === 'function') {
    enhancers.push(devToolsExtension());
  }
}

const composedEnhancers = compose(
  applyMiddleware(...middleware),
  ...enhancers,
);

const persistedReducer = persistReducer(persistConfig, rootReducer);

const configureStore = () => {
  const store = createStore(
    persistedReducer,
    initialState,
    composedEnhancers,
  );

  sagaMiddleware.run(rootSaga);

  if (process.env.NODE_ENV !== 'production') {
    if (module.hot) {
      module.hot.accept('./modules', () => {
        store.replaceReducer(persistedReducer);
      });
    }
  }

  const persistor = persistStore(store);

  const onStorageChange = (event) => {
    if (event.key && event.key.indexOf(KEY_PREFIX) === 0) {
      if (event.oldValue === event.newValue) {
        return;
      }

      const statePartial = JSON.parse(event.newValue);
      if (statePartial) {
        const patientTabsFromStorage = JSON.parse(statePartial.routerParams).tabs.patient;
        const patientTabsFromCurrentState = store.getState().routerParams.tabs.patient;
        if (differenceBy(patientTabsFromCurrentState, patientTabsFromStorage, 'ID').length > 0
          || differenceBy(patientTabsFromStorage, patientTabsFromCurrentState, 'ID').length > 0) {
          store.dispatch(setRouterParamsTabs({
            ...JSON.parse(statePartial.routerParams).tabs,
          }));
        }
      }
    }
  };
  window.addEventListener('storage', onStorageChange, false);

  return { store, persistor };
};

export default configureStore;



// WEBPACK FOOTER //
// ./src/redux/configureStore.js